import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { getOfficePortal } from "../../api/officePortalApi";
import { formatPoints } from "../Dashboard/templates/DashboardShared";
import { resolvePortalProductImage } from "./portalProductImages";
import "./DirectorOfficePortal.css";

const formatBdt = (amount) => {
  if (amount == null || amount === "" || !Number.isFinite(Number(amount))) {
    return "Price unavailable";
  }

  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 2,
  }).format(Number(amount));
};

const formatEarnPoints = (points) => {
  if (points == null) return "—";
  return `${formatPoints(points)} pts`;
};

const DirectorOfficePortal = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalData, setPortalData] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;
    const loadPortal = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getOfficePortal(slug);
        if (active) setPortalData(data);
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 403) {
          setError("You do not have access to this director office portal.");
        } else {
          setError("The office portal is currently unavailable. Please try again later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (slug) {
      loadPortal();
    }

    return () => {
      active = false;
    };
  }, [slug]);

  const filteredProducts = useMemo(() => {
    const products = portalData?.products || [];
    const query = search.trim().toLowerCase();
    if (!query) return products;

    return products.filter(
      (product) =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query),
    );
  }, [portalData, search]);

  if (loading) {
    return (
      <div className="portal-page d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-page">
        <div className="portal-error">
          <h4 className="mb-2">Access denied</h4>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  const { office } = portalData || {};
  const products = Array.isArray(portalData?.products) ? portalData.products : [];
  const hasInventory = products.length > 0;

  const renderInventoryAttributes = (product) => {
    const attributes = product.inventory_attributes || {};
    const values = [
      ["Unit", attributes.unit_number],
      ["Purpose", attributes.purpose],
      ["Availability", product.availability_status || attributes.availability_status],
    ].filter(([, value]) => value != null && value !== "");
    const displayAttributeValue = (label, value) =>
      ["Purpose", "Availability"].includes(label)
        ? String(value).replace(/[_]+/g, " ")
        : String(value);

    if (!values.length) return null;

    return (
      <dl className="portal-card__pricing" aria-label="Product details">
        {values.map(([label, value]) => (
          <div className="portal-card__row" key={label}>
            <dt>{label}</dt>
            <dd className="mb-0">{displayAttributeValue(label, value)}</dd>
          </div>
        ))}
      </dl>
    );
  };

  return (
    <div className="portal-page">
      <section className="portal-hero">
        <span className="portal-hero__eyebrow">Director Office Portal</span>
        <h1>{office?.name}</h1>
        <div className="portal-hero__meta">
          <span>
            <strong>Company:</strong> {office?.company?.name}
          </span>
          {office?.director && (
            <span>
              <strong>Director:</strong> {office.director.name}
            </span>
          )}
          <span>
            <strong>Products:</strong> {products.length}
          </span>
        </div>
      </section>

      <div className="portal-toolbar">
        <div>
          <h5 className="mb-1">Products you can sell</h5>
          <p className="text-muted mb-0">
            Browse available products from your company.
          </p>
        </div>
        <input
          type="search"
          className="form-control"
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {!hasInventory ? (
        <div className="portal-empty">
          <p className="mb-0">There are no available products for this company right now.</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="portal-empty">
          <p className="mb-0">No products match your search.</p>
        </div>
      ) : (
        <div className="portal-grid">
          {filteredProducts.map((product) => (
            <Link
              key={product.id}
              to={`/office-portal/${slug}/products/${encodeURIComponent(product.id)}`}
              className="portal-card"
            >
              <div className="portal-card__image-wrap">
                {resolvePortalProductImage(product) ? (
                  <img
                    src={resolvePortalProductImage(product)}
                    alt={product.name}
                  />
                ) : (
                  <div
                    aria-label="No product image available"
                    className="portal-card__image-placeholder"
                  />
                )}
              </div>
              <div className="portal-card__body">
                <h3 className="portal-card__title">{product.name}</h3>
                {(product.service_name || product.category_name) && (
                  <p className="portal-card__desc mb-2">
                    {product.service_name || product.category_name}
                  </p>
                )}
                <p className="portal-card__desc">{product.description}</p>
                {renderInventoryAttributes(product)}
                <div className="portal-card__pricing">
                  <div className="portal-card__row">
                    <span>Price</span>
                    <strong>{formatBdt(product.price_bdt)}</strong>
                  </div>
                  <div className="portal-card__row">
                    <span>Estimated commission</span>
                    <span className="portal-card__commission">
                      {product.estimated_commission_points == null
                        ? "Unavailable"
                        : formatEarnPoints(product.estimated_commission_points)}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default DirectorOfficePortal;
